<template>
	<v-main>
		<v-container fluid fill-height>
			<v-layout justify-center align-center>
				<v-card width="100%" outlined elevation-0>
					<v-toolbar flat class="border_bottom" shaped>
						<v-btn icon class="mr-1" :title="backLabel" @click="back"><v-icon>mdi-arrow-left</v-icon></v-btn>
						<v-toolbar-title>Lista clienti</v-toolbar-title>
						<v-spacer></v-spacer>
						<v-toolbar-items>
							<v-menu offset-y left>
								<template v-slot:activator="{ on, attrs }">
									<v-btn text v-bind="attrs" title="Esporta" v-on="on"><v-icon>mdi-arrow-collapse-down</v-icon></v-btn>
								</template>
								<v-list dense>
									<v-list-item link @click="esporta('csv')">
										<v-list-item-title>Esporta in CSV</v-list-item-title>
									</v-list-item>
									<v-list-item link @click="esporta('excel')">
										<v-list-item-title>Esporta in Excel</v-list-item-title>
									</v-list-item>
								</v-list>
							</v-menu>
						</v-toolbar-items>
					</v-toolbar>

					<v-col cols="12">
						<v-row>
							<v-col cols="12" md="3">
								<v-text-field
									v-model="searchfield"
									label="Cerca nome o email"
									outlined
									dense
									hide-details
									clearable
									append-icon="mdi-magnify"
									@click:append="getSearch()"
									@keyup.enter="getSearch()"
									@click:clear="pulisciRicerca"
								/>
							</v-col>
							<v-col cols="12" md="3">
								<v-select v-model="settings.filters.segment" :items="segmentItems" label="Segmento" outlined dense hide-details />
							</v-col>
							<v-col cols="12" md="2">
								<v-text-field v-model="settings.filters.platform" label="Canale" outlined dense hide-details clearable placeholder="es. Amazon" />
							</v-col>
							<v-col cols="12" md="2">
								<v-text-field v-model="settings.filters.country" label="Nazione (IT, DE…)" outlined dense hide-details clearable maxlength="2" />
							</v-col>
							<v-col cols="12" md="2" class="d-flex align-center">
								<v-switch v-model="settings.filters.oneTime" color="primary" inset hide-details label="Una sola volta" class="mt-0" />
							</v-col>
						</v-row>
					</v-col>

					<v-data-table
						:headers="headers"
						:items="items"
						:options.sync="settings.options"
						:server-items-length="total"
						must-sort
						class="elevation-0"
						:footer-props="{ 'items-per-page-options': listSize }"
						item-key="key"
						no-data-text="Nessun cliente con questi filtri"
					>
						<template v-slot:[`item.menu`]="{ item }">
							<v-menu offset-y>
								<template v-slot:activator="{ on, attrs }">
									<v-btn icon small v-bind="attrs" title="Azioni" v-on="on"><v-icon small>mdi-dots-vertical</v-icon></v-btn>
								</template>
								<v-list dense>
									<v-list-item link :disabled="!item.customerId" @click="apriCliente(item)">
										<v-list-item-icon><v-icon small>mdi-account-outline</v-icon></v-list-item-icon>
										<v-list-item-title>{{ item.customerId ? 'Apri cliente' : 'Non registrato sul sito' }}</v-list-item-title>
									</v-list-item>
									<v-list-item link :disabled="!item.email" @click="apriOrdini(item)">
										<v-list-item-icon><v-icon small>mdi-receipt-text-outline</v-icon></v-list-item-icon>
										<v-list-item-title>Apri ordini</v-list-item-title>
									</v-list-item>
								</v-list>
							</v-menu>
						</template>
						<template v-slot:[`item.name`]="{ item }">
							<div>{{ item.name || '—' }}</div>
							<div class="cl-muted">{{ item.email || item.key.replace('ebay:', 'eBay: ') }}</div>
						</template>
						<template v-slot:[`item.country`]="{ item }">{{ item.country || '—' }}</template>
						<template v-slot:[`item.platforms`]="{ item }">{{ (item.platforms || []).map(sourceName).join(', ') }}</template>
						<template v-slot:[`item.spend`]="{ item }">{{ formatEurCents(item.spend) }}</template>
						<template v-slot:[`item.lastOrderAt`]="{ item }">
							<div class="text-no-wrap">{{ item.lastOrderAt | moment('DD/MM/YYYY') }}</div>
							<div class="cl-muted">{{ item.recencyDays }} giorni fa</div>
						</template>
						<template v-slot:[`item.rfm`]="{ item }">
							<v-chip x-small label outlined>{{ segmentLabel(item.rfm && item.rfm.segment) }}</v-chip>
						</template>
					</v-data-table>
				</v-card>
			</v-layout>
		</v-container>
	</v-main>
</template>

<script>
import _ from 'lodash';
import { mapActions, mapMutations } from 'vuex';
import { listRouteState } from '@/components/core/list/listRouteState';
import { openWithReturn, returnInfo, goBackWithResult } from '@/components/core/pageReturn';
import { formatEurCents, sourceName } from '@/components/finance/format';

const LIST_SIZE = [25, 50, 100];

export default {
	name: 'FinanceCustomersList',
	// filtri, pagina e ordinamento nell'URL: un refresh o il ritorno da una scheda li ritrovano
	mixins: [listRouteState({ filters: { segment: 'string', platform: 'string', country: 'string', oneTime: 'boolean' }, perPageOptions: LIST_SIZE })],
	data() {
		return {
			settings: this.listSettingsFromRoute({
				search: '',
				filters: { segment: '', platform: '', country: '', oneTime: false },
				options: { sortBy: ['spend'], sortDesc: [true], page: 1, itemsPerPage: 25 },
			}),
			ret: returnInfo(this.$route),
			listSize: LIST_SIZE,
			searchfield: '',
			items: [],
			total: 0,
			segments: [],
			headers: [
				{ text: '', value: 'menu', sortable: false, width: 44 },
				{ text: 'Cliente', value: 'name' },
				{ text: 'Nazione', value: 'country', sortable: false },
				{ text: 'Canali', value: 'platforms', sortable: false },
				{ text: 'Ordini', value: 'orders', align: 'end' },
				{ text: 'Spesa', value: 'spend', align: 'end' },
				{ text: 'Ultimo ordine', value: 'lastOrderAt' },
				{ text: 'Segmento', value: 'rfm', sortable: false },
			],
		};
	},
	computed: {
		backLabel() {
			return this.ret ? this.ret.label : 'Torna al report clienti';
		},
		segmentItems() {
			return [{ value: '', text: 'Tutti' }, ...this.segments.map(s => ({ value: s.id, text: s.label }))];
		},
	},
	async mounted() {
		this.searchfield = this.settings.search || '';
		this.listLoad();
		try {
			this.segments = await this.fetchSegments();
		} catch (err) {
			this.errore(err);
		}
	},
	methods: {
		...mapMutations({ setPreload: 'settings/setPreload', setLogout: 'profile/setLogout' }),
		...mapActions({ fetchList: 'finance/fetchCustomersList', fetchSegments: 'finance/fetchSegments' }),
		formatEurCents,
		sourceName,
		segmentLabel(id) {
			return (this.segments.find(s => s.id === id) || { label: id || '—' }).label;
		},
		getDataFromApi(preload = true) {
			if (preload) this.setPreload(true);
			return this.fetchList(this.settings)
				.then(res => {
					this.items = res.items;
					this.total = res.total;
				})
				.catch(err => this.errore(err))
				.finally(() => preload && this.setPreload(false));
		},
		getSearch() {
			this.settings.search = _.size(this.searchfield) >= 3 ? this.searchfield : '';
		},
		pulisciRicerca() {
			this.searchfield = '';
			this.getSearch();
		},
		back() {
			if (this.ret) goBackWithResult(this.$router, this.ret, '', null);
			else this.$router.push('/finance/customers');
		},
		apriCliente(item) {
			openWithReturn(this, { path: `/customers/${item.customerId}` }, { label: 'Torna alla lista clienti' });
		},
		apriOrdini(item) {
			openWithReturn(this, { path: '/orders', query: { q: item.email } }, { label: 'Torna alla lista clienti' });
		},
		esporta(format) {
			const f = this.settings.filters;
			this.exportAsync('customersreport', format, {
				segment: f.segment || undefined,
				platform: f.platform || undefined,
				country: f.country || undefined,
				oneTime: f.oneTime || undefined,
				search: this.settings.search || undefined,
			});
		},
		errore(err) {
			const res = err.response;
			if (res && res.status === 401) {
				this.setLogout();
				this.$router.push('/login');
				return;
			}
			this.$store.dispatch('settings/updateSnackbar', { color: 'error', text: (res && res.data && res.data.message) || err.message, snackbar: true, mode: 'multi-line', timeout: 5000 });
		},
	},
};
</script>

<style scoped>
.cl-muted {
	font-size: 12px;
	color: #5f6b7a;
}
</style>
